import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getTasks } from '../utils/api';

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Atajo de teclado Cmd/Ctrl + K
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTasks = async () => {
      setLoading(true);
      try {
        const res = await getTasks();
        const tasks = res.data || [];
        
        const filtered = tasks.filter(task => {
          const searchText = query.toLowerCase();
          return (
            task.title.toLowerCase().includes(searchText) ||
            (task.description && task.description.toLowerCase().includes(searchText))
          );
        });
        
        setResults(filtered.slice(0, 10)); // Max 10 resultados
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchTasks, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const highlightMatch = (text, query) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="bg-primary/30 text-primary font-bold">{part}</mark>
        : part
    );
  };

  const handleSelectTask = (taskId) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/tasks`); // Próximo: abrir TaskDrawer directamente
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl hover:bg-white/10 transition-all border border-white/10"
        title="Buscar (Cmd/Ctrl + K)"
      >
        <svg className="w-5 h-5" style={{ color: '#A0F0D0' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="text-sm font-semibold hidden md:inline" style={{ color: '#A0F0D0' }}>
          Buscar tareas...
        </span>
        <span className="text-xs px-2 py-0.5 bg-white/10 rounded font-mono hidden md:inline" style={{ color: '#666' }}>
          ⌘K
        </span>
      </button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center pt-20"
        onClick={() => setIsOpen(false)}
      >
        <motion.div
          initial={{ scale: 0.9, y: -20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: -20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl"
        >
          <div className="decko-card p-4">
            {/* Search Input */}
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar en título, descripción..."
                className="input-decko flex-1"
                autoFocus
              />
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ESC
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Buscando...
                </div>
              ) : query.length < 2 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Escribe al menos 2 caracteres para buscar
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="text-muted-foreground">Sin resultados para "{query}"</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map(task => (
                    <motion.button
                      key={task.id}
                      whileHover={{ x: 4, scale: 1.01 }}
                      onClick={() => handleSelectTask(task.id)}
                      className="w-full text-left p-4 rounded-xl bg-secondary hover:bg-muted transition-all"
                    >
                      <div className="font-semibold text-foreground mb-1">
                        {highlightMatch(task.title, query)}
                      </div>
                      {task.description && (
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {highlightMatch(task.description, query)}
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          task.status === 'Completada' ? 'bg-success/10 text-success' :
                          task.status === 'Devuelta' ? 'bg-destructive/10 text-destructive' :
                          task.status === 'En Espera' ? 'bg-warning/10 text-warning' :
                          'bg-info/10 text-info'
                        }`}>
                          {task.status}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                          {task.priority}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>Mostrando {results.length} resultado{results.length !== 1 ? 's' : ''}</span>
              <span>
                <kbd className="px-2 py-1 bg-secondary rounded font-mono">↑↓</kbd> navegar · 
                <kbd className="px-2 py-1 bg-secondary rounded font-mono ml-1">↵</kbd> abrir
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GlobalSearch;
